import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useCartStore } from '@/stores/cartStore'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useClickOutside } from '@/hooks/useClickOutside'
import { useScentProfileStore } from '@/stores/scentProfileStore'
import { useSettings, type NavCategory } from '@/hooks/useSettings'
import { IconButton } from '@/components/ui/IconButton'
import { BagIcon } from '@/components/ui/BagIcon'
import { InlineSearch } from './InlineSearch'
import { SearchOverlay } from './SearchOverlay'
import { Portal } from '@/components/ui/Portal'
import { cn } from '@/lib/utils'
import { Menu, X, ShoppingBag, User, Heart, Search } from 'lucide-react'

const FALLBACK_CATEGORIES: NavCategory[] = [
  { key: 'shop', label: 'Shop', href: '/shop', visible: true },
  { key: 'new', label: 'New', href: '/shop?tag=new', visible: true },
  { key: 'bestsellers', label: 'Bestsellers', href: '/shop?tag=bestseller', visible: true },
  { key: 'collections', label: 'Collections', href: '/collections', visible: true },
  { key: 'quiz', label: 'Scent Quiz', href: '/scent-quiz', visible: true },
  { key: 'gifts', label: 'Gifts', href: '/shop?tag=gift', visible: true },
]

export function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  useClickOutside(mobileMenuRef, () => setMobileOpen(false), mobileOpen)

  const { toggleCart, items } = useCartStore()
  const { user } = useAuthStore()
  const { openAuthModal, navTheme } = useUIStore()
  const { wishlistIds } = useScentProfileStore()
  const settings = useSettings()

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0)
  const wishlistCount = wishlistIds.length
  const onDark = navTheme === 'dark'

  const categories = useMemo(
    () =>
      (settings.nav_categories ?? FALLBACK_CATEGORIES)
        .filter((cat) => cat.visible !== false)
        .slice(0, 5),
    [settings.nav_categories]
  )

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname, location.search])

  // Flat-edged pill containers: a crisp tinted box (no rounded corners) that
  // sets the two nav groups apart from the transparent header.
  const pillClass =
    'hidden h-12 items-center gap-0.5 border border-line/70 bg-paper px-1.5 lg:inline-flex'
  const pillItem =
    'inline-flex items-center px-3 text-xs font-semibold uppercase tracking-[0.14em] text-carbon transition-colors hover:text-wd-orange'

  // Glass bar spans the full nav width and matches the opaque pill height so
  // all three parts read as one continuous floating bar.
  const glassBarClass =
    'absolute inset-0 h-12 border-0 rounded-none ' +
    'bg-[rgba(255,244,236,0.25)] backdrop-blur-[12px] saturate-[1.2] ' +
    '[-webkit-backdrop-filter:blur(12px)_saturate(1.2)] [backdrop-filter:blur(12px)_saturate(1.2)]'

  // Mobile: opaque pill for the right icons cluster.
  const mobileRightPillClass =
    'flex h-12 items-center gap-1 border border-line/70 bg-paper px-2 lg:hidden'

  return (
    <>
      {/* Single floating bar: glass background spans the full width, with the
          opaque left and right pills sitting flush on it. All pieces share the
          same 48px height so the edges align perfectly. Desktop floats down
          from the top; mobile stays flush to the top edge and spans end to end. */}
      <nav className="relative z-50 lg:pt-2">
        <div className="relative mx-auto grid h-12 max-w-content grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch gap-0 px-0 lg:px-10">
          {/* Full-width glass bar behind everything. */}
          <div className={cn(glassBarClass, 'col-start-1 col-span-3 row-start-1')} aria-hidden="true" />

          {/* Left: desktop categories pill, full height, flush with the glass bar. */}
          <div className="relative col-start-1 row-start-1 hidden items-stretch lg:flex">
            <div className={pillClass}>
              {categories.map((cat) => (
                <Link key={cat.key} to={cat.href} className={cn(pillItem, 'h-9 self-center')}>
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Wordmark: left-aligned on mobile, perfectly centered on desktop.
              Sits on top of the glass bar; no background of its own. */}
          <Link
            to="/"
            aria-label="Little Sky – home"
            className="relative col-start-1 row-start-1 ml-3 flex items-center justify-self-start lg:col-start-2 lg:mx-16 lg:ml-0 lg:justify-self-center"
          >
            <img
              src="/Little-sky-wordmark.png"
              alt="Little Sky"
              className={cn(
                // The brand asset ships white-on-transparent, so it is forced to a
                // known colour rather than filtered for its current pixels:
                // black over the light nav, white once the nav sits on a dark
                // background (brightness first, then invert, per filter order).
                'h-7 w-auto object-contain transition-all duration-300',
                onDark ? 'brightness-0 invert' : 'brightness-0'
              )}
            />
          </Link>

          {/* Right: actions. Desktop uses the opaque pill; mobile uses the opaque right pill. */}
          <div className="relative col-start-3 row-start-1 flex items-center justify-end gap-2">
            <div className={pillClass}>
              <InlineSearch onOpenOverlay={() => setSearchOpen(true)} navColor="text-carbon" scrolled={scrolled} />
              <IconButton
                icon={<Heart className="h-[18px] w-[18px]" />}
                label="Wishlist"
                badge={wishlistCount}
                size="sm"
                variant="ghost"
                onClick={() => navigate('/wishlist')}
                className="text-carbon"
              />
              <IconButton
                icon={<User className="h-[18px] w-[18px]" />}
                label="Account"
                size="sm"
                variant="ghost"
                onClick={() => (user ? navigate('/account/dashboard') : openAuthModal('/account/dashboard'))}
                className="text-carbon"
              />
              <IconButton
                icon={<BagIcon />}
                label="Cart"
                badge={cartCount}
                size="sm"
                variant="ghost"
                onClick={() => toggleCart(true)}
                className="text-carbon"
              />
            </div>

            <div className={mobileRightPillClass}>
              <IconButton
                icon={<Search className="h-[18px] w-[18px]" />}
                label="Search"
                size="sm"
                variant="ghost"
                onClick={() => setSearchOpen(true)}
                className="text-carbon"
              />
              <IconButton
                icon={<Heart className="h-[18px] w-[18px]" />}
                label="Wishlist"
                badge={wishlistCount}
                size="sm"
                variant="ghost"
                onClick={() => navigate('/wishlist')}
                className="text-carbon"
              />
              <IconButton
                icon={<BagIcon />}
                label="Cart"
                badge={cartCount}
                size="sm"
                variant="ghost"
                onClick={() => toggleCart(true)}
                className="text-carbon"
              />
              <IconButton
                icon={mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                label={mobileOpen ? 'Close menu' : 'Open menu'}
                size="sm"
                variant="ghost"
                onClick={() => setMobileOpen((p) => !p)}
                className="text-carbon"
              />
            </div>
          </div>
        </div>

        {/* Mobile full-screen menu — portalled so `inset-0` fills the viewport */}
        <Portal>
          <AnimatePresence>
            {mobileOpen && (
              <motion.div
                ref={mobileMenuRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-40 bg-paper pt-24 lg:hidden"
              >
                <div className="flex h-full flex-col overflow-y-auto px-6 pb-10">
                  <div className="flex flex-col gap-2 py-8">
                    {categories.map((cat) => (
                      <Link
                        key={cat.key}
                        to={cat.href}
                        onClick={() => setMobileOpen(false)}
                        className="font-display text-4xl normal-case text-carbon transition-colors hover:text-wd-orange"
                      >
                        {cat.label.toLowerCase()}
                      </Link>
                    ))}
                  </div>

                  <div className="mt-auto grid grid-cols-2 gap-3 border-t border-line/70 pt-8">
                    <button
                      onClick={() => {
                        setMobileOpen(false)
                        setSearchOpen(true)
                      }}
                      className="flex items-center justify-center gap-2 border border-line/70 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-carbon"
                    >
                      Search
                    </button>
                    <Link
                      to="/wishlist"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 border border-line/70 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-carbon"
                    >
                      <Heart className="h-4 w-4" /> Wishlist
                    </Link>
                    <Link
                      to="/account/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 border border-line/70 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-carbon"
                    >
                      <User className="h-4 w-4" /> Account
                    </Link>
                    <button
                      onClick={() => {
                        setMobileOpen(false)
                        toggleCart(true)
                      }}
                      className="flex items-center justify-center gap-2 border border-line/70 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-carbon"
                    >
                      <ShoppingBag className="h-4 w-4" /> Bag ({cartCount})
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Portal>
      </nav>

      <Portal>
        <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      </Portal>
    </>
  )
}